/**
 * GLM Image on fal.ai (U7). A single-model node (GLMImage), text-to-image only.
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * Replaces (after C5): falai.ts async-image arm `case 'glm-image'` (:966-971), the sync arm
 * (:484-491), ProviderService.generateImageFalAI's generic builder for this id.
 *
 * Legacy wire (oracle snapshot 2026-09-19, __schemas__/fal-ai__glm-image.json):
 *   both      prompt, image_size (preset from aspectRatio), num_images: 1, enable_safety_checker: false
 *   editor    + num_inference_steps (QUALITY_STEPS: fast 10 / balanced 30 / high 50), guidance_scale 1.5,
 *             output_format 'jpeg', enable_prompt_expansion false. Input images are ignored
 *             (glm has no image-to-image endpoint, so the route stays on t2i and sends none).
 *   headless  + output_format 'png', negative_prompt when supplied, and image_urls = every input
 *             image on the SAME t2i endpoint (resolveFalImageEndpoint has no i2i row for glm).
 *             image_urls and negative_prompt are absent from the schema: dead keys.
 */
import type { ModelDescriptor } from '../types';
import { FAL_IMAGE_SIZE_PRESETS } from '../_shared';

export const GLM_IMAGE: ModelDescriptor = {
  id: 'glm-image', provider: 'falai', face: 'image', displayName: 'GLM Image', ledgerName: 'GLM Image', status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    // Editor: no media (the route stays on t2i and sends none). Headless: the generic builder posts
    // every input as image_urls on this same t2i endpoint — a dead key (absent from GlmImageInput,
    // fal drops it). Headless-only media wire (U2-MAPPERS), so no other surface can emit it.
    t2i: { endpoint: 'fal-ai/glm-image', ports: [], media: {},
           mediaBySurface: { headless: { refs: { field: 'image_urls', many: true, encode: 'image' } } } },
  },
  params: [
    { id: 'aspectRatio', type: 'enum', values: ['1:1', '9:16', '16:9', '4:3', '3:4'], default: '1:1',
      invalid: { coerce: { kind: 'default' } },
      wire: { kind: 'imageSize', field: 'image_size', presets: FAL_IMAGE_SIZE_PRESETS, fallback: 'square_hd' },
      ui: { label: 'Aspect ratio', tier: 'basic' } },
    { id: 'quality', type: 'enum', values: ['fast', 'balanced', 'high'], default: 'balanced', invalid: { coerce: { kind: 'default' } },
      wire: false, ui: { label: 'Quality', tier: 'basic' } },   // editor-only steps: legacy.wire
    { id: 'resolution', type: 'string', default: null, role: 'tier', invalid: 'passthrough', wire: false },   // billing-only ladder axis
    { id: 'negativePrompt', type: 'string', default: null, invalid: 'passthrough', wire: false },           // headless legacy.wire only
  ],
  fixed: { num_images: 1, enable_safety_checker: false },
  legacy: {
    wire: [
      { field: 'num_inference_steps', fromParam: 'quality', map: { fast: 10, balanced: 30, high: 50 }, surface: 'editor',
        why: "editor QUALITY_STEPS['glm-image'] (falai.ts:94); headless leaves fal's default 30", retire: 'owner-approval' },
      { field: 'guidance_scale', value: 1.5, surface: 'editor', why: 'editor constant (= fal default 1.5); headless omits it', retire: 'inert-cleanup' },
      { field: 'output_format', value: 'jpeg', surface: 'editor', why: "editor constant 'jpeg' (falai.ts:969)", retire: 'owner-approval' },
      { field: 'enable_prompt_expansion', value: false, surface: 'editor', why: 'editor constant (= fal default false)', retire: 'inert-cleanup' },
      { field: 'output_format', value: 'png', surface: 'headless', why: "headless generic builder 'png' (ProviderService.ts:1024)", retire: 'owner-approval' },
      { field: 'negative_prompt', fromParam: 'negativePrompt', surface: 'headless',
        why: 'headless generic builder (ProviderService.ts:1025); absent from GlmImageInput', retire: 'inert-cleanup' },
    ],
    notes: ['Headless image_urls on the t2i endpoint is a dead key (absent from GlmImageInput): input images are silently ignored.'],
  },
  pricing: {
    unit: 'generation', units: { fixed: 1 }, rounding: 'generic', policy: 'fal-image-35',
    skus: [{
      id: 'glm-image', rate: { ladder: { base: 23, by: 'resolution', curve: 'legacy-resolution-v1' } },
      vendor: { usd: 0.05, per: 'megapixel', field: 'docs',
                source: 'fal-ai/glm-image (fal Platform API GET /v1/models/pricing unit_price 0.05/megapixels, 2026-09-19); the model page carries no pricingInfoOverride',
                verified: '2026-09-19' },
      marginException: 'live 23; policy ceil(0.05 x 300 / 0.65) = 24. P1 is price-neutral: owner decision',
      registry: { displayName: 'GLM Image', operationType: 'image', flatRate: false },
    }],
  },
  knowledge: { summary: 'GLM Image (Zhipu): text-to-image with strong text rendering. No reference images.' },
  schemaVerified: '2026-09-19',
};
